import has from 'lodash-es/has';
import isEmpty from 'lodash-es/isEmpty';
import isFunction from 'lodash-es/isFunction';
import isNumber from 'lodash-es/isNumber';

export { isEmpty, isFunction, isNumber };

export const getIn = (o, path, fallback) =>
  path.reduce((xs, x) => (xs && xs[x] ? xs[x] : fallback), o);

export const isEmptyObject = (obj) =>
  obj && Object.keys(obj).length === 0 && obj.constructor === Object;

export function isNullOrUndefined(x) {
  return x === null || x === undefined;
}

export function isNullOrFalse(x) {
  return x === null || x === false;
}

export function isInteger(x) {
  return x % 1 === 0;
}

export function breakWord(word, maxSize, defaultSuffix?) {
  if (maxSize < 2) {
    console.error("breakWord doesn't work with maxSize < 2");
    return [];
  }

  const suffix = defaultSuffix || '-';
  const chunks = [];
  const maxSuffixedSize = maxSize - 1;

  let isLastChunk;
  let size;
  let maybeSuffix;

  for (let offset = 0; offset < word.length; offset += maxSuffixedSize) {
    isLastChunk = offset + maxSuffixedSize >= word.length;
    size = isLastChunk ? maxSize : maxSuffixedSize;
    maybeSuffix = isLastChunk ? '' : suffix;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    chunks.push(`${word.substr(offset, size)}${maybeSuffix}`);
  }

  return chunks;
}

export function breakString(str, maxSize) {
  const words = str.split(/\s+/);
  const lines = [];
  let lineIdx = 0;
  let word;

  while (words.length > 0) {
    word = words.shift();

    if (word.length > maxSize) {
      breakWord(word, maxSize)
        .reverse()
        .forEach((wordChunk) => {
          words.unshift(wordChunk);
        });

      // eslint-disable-next-line no-continue
      continue;
    }

    if (
      lines.length === lineIdx + 1 &&
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      lines[lineIdx].length > 0 &&
      `${lines[lineIdx]} ${word}`.length >= maxSize
    ) {
      lineIdx += 1;
    }

    if (lines.length === 0 || lines.length === lineIdx) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      lines.push(word);
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      lines[lineIdx] += ` ${word}`;
    }
  }

  return lines;
}

export function isBlank(x) {
  return (
    x !== 0 &&
    x !== false &&
    (!x || (x.isEmpty && x.isEmpty()) || (has(x, 'length') && isEmpty(x)))
  );
}

export function isPresent(x) {
  return !isBlank(x);
}

export function deleteNullValues(map) {
  return map.reduce(
    (nonNullAttributes, v, k) =>
      v === null ? nonNullAttributes.delete(k) : nonNullAttributes,
    map
  );
}

export function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function filterRecordsBy(
  records,
  fieldFilterFn,
  fieldPaths,
  pattern,
  includeUnconditionallyFn
) {
  const patternTokens = pattern.toLowerCase().split(/\s+/);

  return records.filter((record) => {
    if (includeUnconditionallyFn && includeUnconditionallyFn(record)) {
      return true;
    }

    if (fieldFilterFn && !fieldFilterFn(record)) {
      return false;
    }

    return fieldPaths.some((fieldPath) => {
      const fieldValue = record.getIn(fieldPath);

      if (isBlank(fieldValue)) {
        return false;
      }

      const lowerCaseFieldValue = fieldValue.toLowerCase();

      return patternTokens.every((token) =>
        lowerCaseFieldValue.includes(token)
      );
    });
  });
}

export function urlWithoutQueryParam(urlString, queryParamName) {
  const url = new URL(urlString);
  const queryParams = new URLSearchParams(url.search);
  queryParams.delete(queryParamName);
  url.search = queryParams.toString();
  return url;
}

export function isServer() {
  return typeof window === 'undefined';
}

export async function sleep(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

export async function withMinimumDelay<T>(ms: number, promise: Promise<T>) {
  const [p] = await Promise.all([promise, sleep(ms)]);

  return p;
}

export function getElementsAround(coll, target, extractValueFn) {
  const index = coll.findIndex((entry) => extractValueFn(entry) > target);
  if (index === -1 || index === 0 || index === coll.length) {
    return null;
  }
  return [coll.get(index - 1), coll.get(index)];
}

export function interpolate(
  startEpoch,
  endEpoch,
  startValue,
  endValue,
  targetEpoch
) {
  const ratio = (targetEpoch - startEpoch) / (endEpoch - startEpoch);
  return startValue + ratio * (endValue - startValue);
}
